import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import type { CollapseProps } from './types';

export const Collapse = forwardRef<HTMLDivElement, CollapseProps>(
  (
    {
      trigger,
      children,
      timeout = 0,
      open: controlledOpen,
      onOpenChange,
      unmountOnExit = false,
      defaultOpen,
      ...props
    },
    ref,
  ) => {
    const [internalOpen, setInternalOpen] = useState(!!defaultOpen);
    const isOpen = controlledOpen ?? internalOpen;
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState('0px');

    const handleOpenChange = (next: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(next);
      onOpenChange?.(next);
    };

    useLayoutEffect(() => {
      const el = contentRef.current;
      if (!el) return;

      if (isOpen) {
        const h = el.scrollHeight;
        setHeight(h + 'px');
        const id = setTimeout(() => setHeight('auto'), timeout);
        return () => clearTimeout(id);
      } else {
        if (height === 'auto') {
          setHeight(el.scrollHeight + 'px');
          requestAnimationFrame(() => setHeight('0px'));
        } else {
          setHeight('0px');
        }
      }
    }, [isOpen, children, timeout, height]);

    return (
      <Collapsible.Root
        {...props}
        ref={ref}
        open={controlledOpen}
        defaultOpen={controlledOpen === undefined ? defaultOpen : undefined}
        onOpenChange={handleOpenChange}
      >
        {trigger && (
          <Collapsible.Trigger asChild>{trigger}</Collapsible.Trigger>
        )}

        {(!unmountOnExit || isOpen) && (
          <Collapsible.Content forceMount asChild>
            <div
              ref={contentRef}
              style={{
                height,
                overflow: height === '0px' ? 'hidden' : 'visible',
                // TODO: animation is not working because React applies the
                // effect before the browser ever paints the 0px state.
                transition: `height ${timeout}ms ease`,
              }}
            >
              {children}
            </div>
          </Collapsible.Content>
        )}
      </Collapsible.Root>
    );
  },
);
